import { useEffect, useState } from 'react';
import type { FieldMetadata, FieldValue, ObjectMetadata } from '../../data/client';
import { useCreateRecord, useFields } from '../../data/hooks';
import { FieldDisplay } from '../../fields/display/FieldDisplay';
import { FieldProvider } from '../../fields/FieldContext';
import { Button, Dialog, FormField, Input, Skeleton, useToast } from '../../ui';

/**
 * THE ONE CREATE DIALOG (audit P1-3, P1-4; owner's decision, the Attio/HubSpot pattern).
 *
 * Every create in this product used to MINT FIRST AND ASK NEVER: "New" wrote an empty row
 * and navigated to it. The audit measured what that costs - the minted record is on page
 * one of no list (the list is sorted, it is row 121 of 120) and in no board column (it has
 * no stage), so a user who presses Back sees nothing and presses New again. Every attempt
 * left a row behind.
 *
 * So the record EXISTS ONLY ON CONFIRM, and the dialog asks the one question that decides
 * whether it can be found again: what is it called. Everything else belongs on the record
 * page - a create dialog that grew a second field would be the record page in a smaller
 * box, and the product already has a record page.
 *
 * THE NAME IS REQUIRED, which is this component's own rule rather than the brief's. The
 * dialog exists to ask one question; confirming it unanswered would mint exactly the
 * nameless row the audit counted, only with a dialog in front of it. The hint says why
 * rather than leaving a disabled button unexplained.
 *
 * WHAT IT DOES AFTER THE WRITE IS THE CALLER'S (`onCreated`), and the two answers are
 * both right for their own surface. From a LIST, the FAB or an empty state the new record
 * is the destination - the user asked for a record and nothing on the list can show it
 * yet. From a RELATED SECTION the parent is the destination: the child appears in the
 * section that made it, which is what the junction picker beside it has always done.
 *
 * INV-10 is preserved from the call sites it replaces: an agent-gated object answers with
 * a PROPOSAL rather than a record, and `onCreated` is not called at all - navigating into
 * a pending change would be navigating into nothing.
 */
export interface CreatePrefill {
  readonly field: FieldMetadata;
  readonly value: FieldValue;
  /**
   * The label a QUERY already resolved for a relation, when the caller has it. Absent,
   * `RelationDisplay` resolves it through the canonical cache, which on a record page is
   * already warm.
   */
  readonly relationLabel?: string | undefined;
}

export interface CreateRecordDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly object: ObjectMetadata;
  /**
   * Values written with the title, and SHOWN: a create opened from a related section
   * carries its parent, one opened from a board column carries that column's option, and
   * a dialog that wrote either of them invisibly would be a dialog that surprises.
   */
  readonly prefill?: readonly CreatePrefill[] | undefined;
  /** The record exists. Navigate to it, or stay and let the list it joined show it. */
  readonly onCreated: (recordId: string) => void;
}

export function CreateRecordDialog({
  open,
  onOpenChange,
  object,
  prefill,
  onCreated,
}: CreateRecordDialogProps) {
  const toast = useToast();
  const fields = useFields(object.id);
  const create = useCreateRecord(object.id);
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Cleared on the OPEN edge, so the second create does not start in the first one's
    // words - and so a failed attempt's message does not outlive the dialog that got it.
    if (open) {
      setTitle('');
      setError(null);
    }
  }, [open]);

  /**
   * An object with no TEXT field has no label identifier (`mapping.ts`), and then there is
   * no question to ask. The dialog still exists, because the prefill and the confirm still
   * do - it says what will happen instead of asking for a name that cannot be stored.
   */
  const titleField = fields.data?.find((field) => field.id === object.labelIdentifierFieldId);
  const trimmed = title.trim();
  const ready = fields.data !== undefined;
  const complete = titleField === undefined || trimmed !== '';

  const submit = () => {
    if (!ready || !complete || create.isPending) return;
    const values: Record<string, FieldValue> = {};
    for (const entry of prefill ?? []) {
      values[entry.field.id] = entry.value;
    }
    if (titleField !== undefined) {
      values[titleField.id] = trimmed;
    }
    create.mutate(values, {
      onSuccess: (result) => {
        onOpenChange(false);
        if (result.status === 'pending_approval') {
          toast({ title: 'Queued for approval', tone: 'neutral' });
          return;
        }
        onCreated(result.record.id);
      },
      // The server's sentence, in the dialog that caused it: a toast behind a modal is a
      // message about this form shown somewhere the form is not.
      onError: (failure) => setError(failure.message),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`New ${object.labelSingular}`}
      description="It is created when you confirm. Everything else can be filled in on its page."
      width="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!ready || !complete}
            onClick={submit}
          >
            Create
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {!ready ? (
          <Skeleton className="h-10" label="Loading the form" />
        ) : titleField === undefined ? (
          <p className="text-sm text-ink-muted">
            {`${object.labelPlural} have no title field, so there is no name to give this one yet. An admin can set one under Configure.`}
          </p>
        ) : (
          <FormField
            label={titleField.label}
            required
            {...(error === null ? {} : { error })}
            hint="A record with no name is hard to find again."
          >
            {(props) => (
              <Input
                {...props}
                value={title}
                invalid={error !== null}
                onChange={(event) => setTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    submit();
                  }
                }}
                autoFocus
              />
            )}
          </FormField>
        )}

        {(prefill ?? []).map((entry) => (
          <PrefilledValue key={entry.field.id} objectId={object.id} prefill={entry} />
        ))}
      </div>
    </Dialog>
  );
}

/**
 * A prefilled value, rendered by the FIELD REGISTRY rather than as text: a relation then
 * says "Talia Morrow" and a select shows its own option chip, which is what the same
 * value looks like everywhere else in the product. Read-only by construction - this is a
 * statement about the record being made, not a second control.
 */
function PrefilledValue({
  objectId,
  prefill,
}: {
  readonly objectId: string;
  readonly prefill: CreatePrefill;
}) {
  return (
    <div className="space-y-1.5">
      <span className="block text-xs font-medium text-ink-muted">{prefill.field.label}</span>
      <div className="text-sm text-ink">
        <FieldProvider
          value={{
            objectId,
            // No record exists yet, which is the whole point of this dialog. The displays
            // that read an id read it off the VALUE; none of them reads this one.
            recordId: '',
            field: prefill.field,
            value: prefill.value,
            onChange: () => undefined,
            readOnly: true,
            humanOwned: false,
            ...(prefill.relationLabel === undefined
              ? {}
              : { relationLabel: prefill.relationLabel }),
          }}
        >
          <FieldDisplay />
        </FieldProvider>
      </div>
    </div>
  );
}
